import { atom } from "jotai";
import {
  IPageSection,
  ISectionGate,
  PAGE_SECTIONS,
  SectionId,
  SectionedPage,
  sectionHref,
} from "@/components/Layouts/pageSections";

// Non-component sidenav exports live here (not in DashboardSideNav.tsx) so that
// file exports only components — otherwise these break React Fast Refresh and
// edits to the sidebar/footer stop hot-reloading.

export interface IEntry extends ISectionGate {
  title: string;
  icon: any;
  href?: string;
  onClick?: () => void;
  tag?: string;
  permissions?: string[];
  id: string;
  is_enterprise_locked?: boolean;
  // Makes the entry a button that expands to these pages instead of a link.
  children?: IEntry[];
}

export const isTutorialStepAtom = atom<number | undefined>(undefined);

// Header and footer are 8px + 32px + 8px with the separator outside, the same
// as the page header beside them, so the lines run straight across.
export const SIDENAV_HEADER_CLASS =
  "box-content h-8 shrink-0 justify-center gap-0 border-b border-border px-2 py-2";
export const SIDENAV_FOOTER_CLASS =
  "box-content h-8 shrink-0 justify-center gap-0 border-t border-border px-2 py-2";

// The small icon buttons in a sidenav's header and lists (pin, back, add).
export const SIDENAV_ICON_BUTTON_CLASS =
  "h-7 w-7 p-0 flex items-center justify-center cursor-pointer text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 [&>svg]:text-sidebar-foreground/70 [&>svg]:hover:text-sidebar-foreground";

// The buttons on a sidenav group's heading row (Inboxes, Open tools).
export const SIDENAV_GROUP_ACTION_CLASS =
  "h-6 w-6 cursor-pointer text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 [&>svg]:text-sidebar-foreground/70 [&>svg]:hover:text-sidebar-foreground";

// A page's section as a sidenav entry, linking to it and gated like it.
export const sectionEntry = <P extends SectionedPage>(
  page: P,
  id: SectionId<P>,
  overrides: Partial<IEntry> = {}
): IEntry => {
  const { path, sections } = PAGE_SECTIONS[page] as {
    path?: string;
    sections: readonly IPageSection[];
  };
  const section = sections.find((s) => s.id === id);
  if (!section) throw new Error(`No section "${id}" on page "${page}"`);
  const { title, icon, ...gate } = section;
  return {
    ...gate,
    id: `${page}-${id}`,
    title,
    icon,
    href: sectionHref(path ?? "", id),
    ...overrides,
  };
};

// The small buttons a sidenav row shows on hover (options, close), filled
// only under the pointer, like the header's icon buttons.
export const SIDENAV_ROW_ACTION_CLASS =
  "cursor-pointer flex size-5 items-center justify-center rounded-sm text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring [&>svg]:size-3.5";
